import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'primary';
}

interface FeedbackState {
  toasts: ToastItem[];
  addToast: (toast: Omit<ToastItem, 'id'>) => string;
  removeToast: (id: string) => void;
  
  // Confirm modal state
  confirmState: {
    isOpen: boolean;
    options: ConfirmOptions;
    resolve: ((value: boolean) => void) | null;
  };
  openConfirm: (options: ConfirmOptions) => Promise<boolean>;
  closeConfirm: (result: boolean) => void;
}

export const useFeedbackStore = create<FeedbackState>((set, get) => ({
  toasts: [],
  addToast: (toast) => {
    const id = Math.random().toString(36).substring(2, 9);
    const duration = toast.duration ?? 3500;
    
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }));

    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }

    return id;
  },
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  confirmState: {
    isOpen: false,
    options: { message: '' },
    resolve: null,
  },
  openConfirm: (options) => {
    return new Promise<boolean>((resolve) => {
      set({
        confirmState: {
          isOpen: true,
          options,
          resolve,
        },
      });
    });
  },
  closeConfirm: (result) => {
    const { resolve } = get().confirmState;
    if (resolve) {
      resolve(result);
    }
    set({
      confirmState: {
        isOpen: false,
        options: { message: '' },
        resolve: null,
      },
    });
  },
}));

// Convenience helpers
export const toast = {
  success: (message: string, title?: string, duration?: number) =>
    useFeedbackStore.getState().addToast({ type: 'success', message, title, duration }),
  error: (message: string, title?: string, duration?: number) =>
    useFeedbackStore.getState().addToast({ type: 'error', message, title, duration }),
  warning: (message: string, title?: string, duration?: number) =>
    useFeedbackStore.getState().addToast({ type: 'warning', message, title, duration }),
  info: (message: string, title?: string, duration?: number) =>
    useFeedbackStore.getState().addToast({ type: 'info', message, title, duration }),
};

export const confirmDialog = (options: ConfirmOptions | string): Promise<boolean> => {
  const opts = typeof options === 'string' ? { message: options } : options;
  return useFeedbackStore.getState().openConfirm(opts);
};
